import { useEffect, useState, useMemo } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Trash2, X, AlertOctagon, CheckCircle2, Clock, ChevronDown, ChevronRight, Pencil, RotateCcw } from "lucide-react";
import ConfirmDialog from "../../components/ConfirmDialog";
import { useCollapsibleGroups } from "../../lib/useCollapsibleGroups";

const CANCEL_REASONS = ["Visa refused", "Participant dropped", "Programme changed", "Health / medical", "Family emergency", "Other"];

const USD = (n) => `$${Number(n || 0).toLocaleString()}`;
const REASONS = ["Visa refused", "Participant dropped", "Duplicate row", "Programme changed", "Other"];

export default function MyApplications({ apps, staffPrograms, onDeleted, onGotoTab }) {
  const statusPill = (s) => s === "approved" ? "bg-emerald-100 text-emerald-800" : s === "rejected" ? "bg-red-100 text-red-800" : s === "cancelled" ? "bg-slate-200 text-slate-700" : "bg-amber-100 text-amber-800";
  const CUR = { USD: "$", EUR: "€", GBP: "£", INR: "₹" };
  // Hide approved applications that already show up in the "Booked by Aii
  // Group" section — they duplicate the same participant twice on the
  // partner dashboard.  Approved rows with no matching booking (transient)
  // still surface so nothing is lost.
  const matchStaff = (a) => (staffPrograms || []).some(p =>
    (p.customer || "").trim().toLowerCase() === (a.name || "").trim().toLowerCase()
    && (p.start_date || "") === (a.start_date || "")
  );
  const visibleApps = apps.filter(a => a.status !== "approved" || !matchStaff(a));
  const hasAny = visibleApps.length > 0 || (staffPrograms && staffPrograms.length > 0);
  const [confirming, setConfirming] = useState(null);
  const [reasonModal, setReasonModal] = useState(null); // { ids, kind: "single" | "bulk" }
  const [reason, setReason] = useState("");
  const [reasonNote, setReasonNote] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  // Cancellation-request state: { appId → { status, id, reason, created_at } }
  const [cxlByApp, setCxlByApp] = useState({});
  // Cancellation state also keyed by booking_id for staff-created rows
  // (they have no linking application).
  const [cxlByBooking, setCxlByBooking] = useState({});
  const [cxlModal, setCxlModal] = useState(null); // { app | booking, target: "app"|"booking", reason, note }
  const { collapsed: collapsedDates, toggle: toggleDate } = useCollapsibleGroups("partner-my-applications");

  // Mount-time fetch — only writes to setCxlByApp/setCxlByBooking
  // (stable state setters), so [] deps are intentional.
  useEffect(() => {
    // Fetch existing cancellation requests so "Request cancellation" buttons
    // on locked rows can flip to "Cancellation requested" when a ticket is
    // already open. Silent on failure — the endpoint 401s for non-partners.
    api.get("/partner-portal/cancellation-requests").then(({ data }) => {
      const byApp = {};
      const byBooking = {};
      for (const r of (data || [])) {
        if (r.application_id) {
          if (!byApp[r.application_id] || (r.created_at || "") > (byApp[r.application_id].created_at || "")) {
            byApp[r.application_id] = r;
          }
        }
        if (r.booking_id) {
          if (!byBooking[r.booking_id] || (r.created_at || "") > (byBooking[r.booking_id].created_at || "")) {
            byBooking[r.booking_id] = r;
          }
        }
      }
      setCxlByApp(byApp);
      setCxlByBooking(byBooking);
    }).catch(() => {});
  }, []);

  const openCxlModal = (a) => setCxlModal({ app: a, target: "app", reason: "", note: "" });
  const openBookingCxlModal = (b) => setCxlModal({ booking: b, target: "booking", reason: "", note: "" });
  const submitCxl = async () => {
    if (!cxlModal) return;
    const reasonText = cxlModal.reason === "Other" ? (cxlModal.note || "").trim() : cxlModal.reason;
    if (!reasonText) return toast.error("Pick a reason so our team can act quickly");
    setBusy(true);
    try {
      if (cxlModal.target === "booking") {
        const { data } = await api.post(`/partner-portal/bookings/${cxlModal.booking.id}/request-cancellation`, { reason: reasonText });
        toast.success("Cancellation request sent to our team — they'll be in touch");
        setCxlByBooking(m => ({ ...m, [cxlModal.booking.id]: { id: data.request_id, status: "open", reason: reasonText, booking_id: cxlModal.booking.id, created_at: new Date().toISOString() } }));
      } else {
        const { data } = await api.post(`/partner-portal/applications/${cxlModal.app.id}/request-cancellation`, { reason: reasonText });
        toast.success("Cancellation request sent to our team — they'll be in touch");
        setCxlByApp(m => ({ ...m, [cxlModal.app.id]: { id: data.request_id, status: "open", reason: reasonText, application_id: cxlModal.app.id, created_at: new Date().toISOString() } }));
      }
      setCxlModal(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to send"); }
    setBusy(false);
  };

  const toggleRow = (id) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const selectAll = () => setSelected(prev => {
    // Bulk-select ignores approved rows — they're locked at both UI + API level.
    const selectable = visibleApps;
    return selectable.length > 0 && selectable.every(a => prev.has(a.id))
      ? new Set()
      : new Set(selectable.map(a => a.id));
  });
  const clearSelected = () => setSelected(new Set());

  const runDeletes = async (ids, reasonText) => {
    setBusy(true);
    try {
      // If ANY selected id is an approved application we tack on
      // `?force=true` — the backend now allows partner-side force
      // deletion (booked participants included) with the whole
      // chain snapshotted to the Recycle Bin. Non-approved rows
      // ignore the flag.
      const q = new URLSearchParams();
      if (reasonText) q.set("reason", reasonText);
      const results = await Promise.allSettled(ids.map(id => {
        const isApproved = apps.find(a => a.id === id)?.status === "approved";
        const params = new URLSearchParams(q);
        if (isApproved) params.set("force", "true");
        const suffix = params.toString();
        return api.delete(`/partner-portal/applications/${id}${suffix ? `?${suffix}` : ""}`);
      }));
      const ok = results.filter(r => r.status === "fulfilled").length;
      const fail = ids.length - ok;
      if (ok > 0) toast.success(`${ok} application${ok === 1 ? "" : "s"} deleted${fail ? ` · ${fail} failed` : ""}`);
      if (fail && ok === 0) toast.error("Delete failed");
      clearSelected();
      onDeleted?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to delete"); }
    finally { setBusy(false); setReasonModal(null); setReason(""); setReasonNote(""); }
  };

  const openReasonModal = (ids, kind) => { setReason(""); setReasonNote(""); setReasonModal({ ids, kind }); };

  const askDelete = (a) => {
    // Approved rows used to be blocked entirely — Founder now allows
    // the partner to force-delete booked participants (Recycle Bin
    // backed). Keep the messaging honest: extra warning + no undo.
    const isApproved = a.status === "approved";
    setConfirming({
      id: a.id,
      title: isApproved
        ? `Force delete confirmed booking for ${a.name}?`
        : `Delete application for ${a.name}?`,
      description: isApproved
        ? "This removes the booked participant AND their linked program booking, sale, arrival and C-form. The whole chain is snapshotted to the Recycle Bin so your Aii coordinator can restore it if this was a mistake."
        : "The application will be removed permanently.\nThis action cannot be undone.",
      action: () => openReasonModal([a.id], "single"),
    });
  };

  // Partner-side "nuke everything I've uploaded" — powered by
  // /api/partner-portal/reset-my-data. Everything gets Recycle-Bin
  // snapshotted so the Founder can restore a mistaken reset.
  const [resetOpen, setResetOpen] = useState(false);
  const [resetType, setResetType] = useState("");
  const [resetBusy, setResetBusy] = useState(false);
  // Staff-booked participants (created by Aii team) — partner can now
  // delete individually or bulk. Recycle-Bin backed so the Aii
  // coordinator can restore a mistaken cancel.
  const [delStaff, setDelStaff] = useState(null);   // program_booking row
  const [bulkDelStaff, setBulkDelStaff] = useState(false);
  const [staffBusy, setStaffBusy] = useState(false);
  const doDelStaff = async (reasonText) => {
    if (!delStaff) return;
    setStaffBusy(true);
    try {
      const q = reasonText ? `?reason=${encodeURIComponent(reasonText)}` : "";
      await api.delete(`/partner-portal/staff-program-bookings/${delStaff.id}${q}`);
      toast.success(`Removed ${delStaff.customer} — restore via your coordinator if this was a mistake.`);
      setDelStaff(null);
      onDeleted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setStaffBusy(false); }
  };
  const doBulkDelStaff = async (reasonText) => {
    setStaffBusy(true);
    try {
      const { data } = await api.post("/partner-portal/staff-program-bookings/bulk-delete", {
        ids: (staffPrograms || []).map(p => p.id),
        reason: reasonText || "",
      });
      toast.success(`Removed ${data.deleted} booked participant${data.deleted === 1 ? "" : "s"} — Recycle-Bin backed.`);
      setBulkDelStaff(false);
      onDeleted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk delete failed");
    } finally { setStaffBusy(false); }
  };
  // Reason-picker piggybacks off the existing REASONS list so the
  // Founder gets the same trend data whether the partner cancelled a
  // pending application or a booked participant. `staffReasonModal`
  // shape: { kind: "single" | "bulk", target?: row }
  const [staffReasonModal, setStaffReasonModal] = useState(null);
  const [staffReason, setStaffReason] = useState("");
  const [staffReasonNote, setStaffReasonNote] = useState("");
  const askDelStaff = (row) => { setDelStaff(row); setStaffReason(""); setStaffReasonNote(""); setStaffReasonModal({ kind: "single", target: row }); };
  const askBulkDelStaff = () => { setBulkDelStaff(true); setStaffReason(""); setStaffReasonNote(""); setStaffReasonModal({ kind: "bulk" }); };
  const submitStaffReason = () => {
    const reasonText = staffReason === "Other" ? (staffReasonNote || "Other").trim() : staffReason;
    setStaffReasonModal(null);
    if (staffReasonModal?.kind === "bulk") doBulkDelStaff(reasonText);
    else doDelStaff(reasonText);
  };
  const CONFIRM_PHRASE = "RESET MY DATA";
  const doResetMyData = async () => {
    setResetBusy(true);
    try {
      const { data } = await api.post("/partner-portal/reset-my-data");
      toast.success(`Reset — ${data.doc_count} row${data.doc_count === 1 ? "" : "s"} archived (${data.program_bookings_cleared} program + ${data.weekend_bookings_cleared} weekend bookings). Restore via your coordinator.`);
      setResetOpen(false); setResetType("");
      onDeleted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally { setResetBusy(false); }
  };

  return (
    <div className="space-y-3">
      {!hasAny && <div className="aii-card p-8 text-center text-sm text-[color:var(--aii-muted)]">No applications yet.</div>}
      {visibleApps.length > 0 && (
        <div className="flex items-center gap-2 text-xs text-[color:var(--aii-muted)]">
          <input type="checkbox" data-testid="app-select-all"
                 checked={visibleApps.length > 0 && visibleApps.every(a => selected.has(a.id))}
                 onChange={selectAll}/>
          <span>Select all ({visibleApps.length})</span>
        </div>
      )}
      {(() => {
        // Group applications by exact start-date so partners see cohorts
        // arriving on the same day clustered together.  Falls back to
        // "No start date" for rows without a start_date.
        if (visibleApps.length === 0) return null;
        const groups = new Map();
        for (const a of visibleApps) {
          const k = (a.start_date || "").slice(0, 10) || "No start date";
          if (!groups.has(k)) groups.set(k, []);
          groups.get(k).push(a);
        }
        const prettyDate = (iso) => {
          if (!iso || iso === "No start date") return "No start date";
          const d = new Date(`${iso}T00:00:00`);
          if (Number.isNaN(d.getTime())) return iso;
          return d.toLocaleDateString(undefined, { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
        };
        const out = [];
        for (const [ymd, list] of [...groups.entries()].sort()) {
          const isCollapsed = collapsedDates.has(ymd);
          out.push(
            <button key={`hdr-${ymd}`} data-testid={`app-date-hdr-${ymd}`}
                 type="button"
                 onClick={() => toggleDate(ymd)}
                 className="w-full text-left text-[11px] uppercase tracking-widest font-bold text-brand pt-2 pb-1 border-b border-[color:var(--aii-border)] flex items-center gap-2 hover:bg-[color:var(--aii-green-tint)]/40 rounded px-1">
              {isCollapsed ? <ChevronRight size={12}/> : <ChevronDown size={12}/>}
              <span>{prettyDate(ymd)}</span>
              <span className="text-[10px] font-normal text-[color:var(--aii-muted)] normal-case tracking-normal">
                · {list.length} application{list.length === 1 ? "" : "s"}
              </span>
            </button>
          );
          if (isCollapsed) continue;
          for (const a of list) out.push(
            <div key={a.id} data-testid={`app-row-${a.id}`}
                 className={`aii-card p-4 ${selected.has(a.id) ? "border-brand bg-[color:var(--aii-green-tint)]/40" : ""}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <input type="checkbox" data-testid={`app-select-${a.id}`}
                         checked={selected.has(a.id)}
                         onChange={() => toggleRow(a.id)}/>
                  <div>
                    <div className="font-semibold">{a.name}</div>
                    <div className="text-xs text-[color:var(--aii-muted)]">{a.program} · {a.destination} / {a.city} · {a.duration_weeks} weeks · {a.start_date} → {a.end_date}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {a.price != null && <span className="text-sm font-semibold text-brand">{USD(a.price)}</span>}
                  <span className={`text-xs px-2 py-0.5 rounded font-semibold uppercase ${statusPill(a.status)}`}>{a.status}</span>
                  {a.status === "approved" ? (
                    <>
                      <span data-testid={`app-locked-${a.id}`}
                            title="This booking is confirmed by our team. To cancel, tap Request cancellation."
                            className="text-[10px] px-2 py-1 rounded bg-slate-100 text-[color:var(--aii-muted)] border border-[color:var(--aii-border)] flex items-center gap-1 font-semibold uppercase">
                        Locked
                      </span>
                      {cxlByApp[a.id]?.status === "open" ? (
                        <span data-testid={`app-cxl-pending-${a.id}`} title={cxlByApp[a.id].reason || "Cancellation requested"}
                              className="text-[10px] px-2 py-1 rounded bg-amber-100 text-amber-800 border border-amber-200 font-semibold uppercase flex items-center gap-1">
                          <Clock size={9}/> Cancel requested
                        </span>
                      ) : cxlByApp[a.id]?.status === "resolved" ? (
                        <span data-testid={`app-cxl-resolved-${a.id}`}
                              className="text-[10px] px-2 py-1 rounded bg-emerald-100 text-emerald-800 border border-emerald-200 font-semibold uppercase flex items-center gap-1">
                          <CheckCircle2 size={9}/> Cancel resolved
                        </span>
                      ) : (
                        <button data-testid={`app-cxl-btn-${a.id}`}
                                onClick={() => openCxlModal(a)}
                                title="Ask your coordinator to cancel this booking"
                                className="text-[10px] px-2 py-1 rounded border border-red-500 text-red-700 hover:bg-red-50 font-semibold uppercase flex items-center gap-1">
                          <AlertOctagon size={9}/> Request cancellation
                        </button>
                      )}
                      <button
                        data-testid={`app-force-delete-${a.id}`}
                        onClick={() => askDelete(a)}
                        title="Force delete this booked participant (Recycle-Bin backed — Aii coordinator can restore)"
                        className="text-red-700 hover:text-white hover:bg-red-600 p-1.5 rounded border border-red-300 hover:border-red-600">
                        <Trash2 size={13}/>
                      </button>
                    </>
                  ) : (
                    <button
                      data-testid={`app-delete-${a.id}`}
                      onClick={() => askDelete(a)}
                      title="Delete application"
                      className="text-[color:var(--aii-muted)] hover:text-red-600 p-1.5 rounded hover:bg-red-50">
                      <Trash2 size={13}/>
                    </button>
                  )}
                </div>
              </div>
              {a.reject_reason && <div className="mt-2 text-xs text-red-700">Reason: {a.reject_reason}</div>}
            </div>
          );
        }
        return out;
      })()}
      {(staffPrograms || []).length > 0 && (
        <div className="pt-3">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
            <div className="text-[11px] uppercase tracking-widest text-[color:var(--aii-muted)] font-semibold">Booked by our team under your account</div>
            <button
              data-testid="staff-bulk-delete"
              onClick={() => askBulkDelStaff()}
              className="text-[10px] px-2 py-1 border border-red-400 text-red-700 rounded hover:bg-red-50 font-semibold uppercase flex items-center gap-1">
              <Trash2 size={10}/> Delete all {staffPrograms.length}
            </button>
          </div>
          <div className="space-y-2">
            {staffPrograms.map(p => {
              const cxl = cxlByBooking[p.id];
              return (
              <div key={`staff-${p.id}`} data-testid={`staff-app-row-${p.id}`} className="aii-card p-4 border-l-4 border-brand">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <div className="font-semibold">{p.customer}</div>
                      {p.start_date && (() => {
                        // Month pill — shows the start-month at a glance
                        // ("Aug 2026") so partners can scan a long list by
                        // batch. Sky palette to match the brand's dashboard.
                        const d = new Date(p.start_date);
                        if (Number.isNaN(d.getTime())) return null;
                        const label = d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
                        return (
                          <span data-testid={`staff-app-month-${p.id}`}
                                title="Programme month"
                                className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-sky-100 text-sky-800 border border-sky-300">
                            {label}
                          </span>
                        );
                      })()}
                    </div>
                    <div className="text-xs text-[color:var(--aii-muted)] mt-0.5">{p.programme} · {p.project_placement || "—"} · {p.start_date || "—"} → {p.end_date || "—"}</div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {p.sale_amount != null && <span className="text-sm font-semibold text-brand">{CUR[p.sale_currency] || p.sale_currency}{Number(p.sale_amount || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>}
                    <span className="text-[10px] uppercase px-2 py-0.5 bg-brand text-white rounded font-semibold">Booked</span>
                    {cxl?.status === "open" ? (
                      <span data-testid={`staff-cxl-pending-${p.id}`} title={cxl.reason || "Cancellation requested"}
                            className="text-[10px] px-2 py-1 rounded bg-amber-100 text-amber-800 border border-amber-200 font-semibold uppercase flex items-center gap-1">
                        <Clock size={9}/> Cancel requested
                      </span>
                    ) : cxl?.status === "resolved" ? (
                      <span data-testid={`staff-cxl-resolved-${p.id}`}
                            className="text-[10px] px-2 py-1 rounded bg-emerald-100 text-emerald-800 border border-emerald-200 font-semibold uppercase flex items-center gap-1">
                        <CheckCircle2 size={9}/> Cancel resolved
                      </span>
                    ) : (
                      <button data-testid={`staff-cxl-btn-${p.id}`}
                              onClick={() => openBookingCxlModal(p)}
                              title="Ask your coordinator to cancel this booking"
                              className="text-[10px] px-2 py-1 rounded border border-red-500 text-red-700 hover:bg-red-50 font-semibold uppercase flex items-center gap-1">
                        <AlertOctagon size={9}/> Request cancellation
                      </button>
                    )}
                    <button data-testid={`staff-edit-btn-${p.id}`}
                            onClick={() => { onGotoTab?.("arrivals"); toast.success("Update flight & hotel in Arrival Info tab"); }}
                            title="Edit flight / hotel / pickup for this booking"
                            className="text-[color:var(--aii-muted)] hover:text-brand p-1.5 rounded hover:bg-[color:var(--aii-green-tint)]">
                      <Pencil size={13}/>
                    </button>
                    <button data-testid={`staff-delete-btn-${p.id}`}
                            onClick={() => askDelStaff(p)}
                            title="Delete this booked participant (Recycle-Bin backed — Aii can restore)"
                            className="text-red-700 hover:text-white hover:bg-red-600 p-1.5 rounded border border-red-300 hover:border-red-600">
                      <Trash2 size={13}/>
                    </button>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        </div>
      )}
      <ConfirmDialog
        open={!!confirming}
        title={confirming?.title}
        description={confirming?.description}
        confirmLabel="Delete application"
        onConfirm={() => { confirming?.action(); setConfirming(null); }}
        onCancel={() => setConfirming(null)}
        testid="app-delete-confirm"
      />

      {cxlModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setCxlModal(null)}>
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-testid="app-cxl-modal">
            <div className="p-4 border-b border-[color:var(--aii-border)] flex items-start justify-between">
              <div>
                <h3 className="font-display text-lg font-semibold flex items-center gap-2"><AlertOctagon size={16} className="text-red-600"/> Request cancellation</h3>
                <p className="text-xs text-[color:var(--aii-muted)] mt-0.5">Our team will handle the refund and system cleanup safely. You&apos;ll be pinged once they action it.</p>
                <p className="text-xs mt-1 font-semibold">For: {cxlModal.target === "booking" ? cxlModal.booking?.customer : cxlModal.app?.name}</p>
              </div>
              <button onClick={() => setCxlModal(null)} className="text-[color:var(--aii-muted)] hover:text-red-600 p-1"><X size={16}/></button>
            </div>
            <div className="p-4 space-y-2">
              {CANCEL_REASONS.map(r => (
                <label key={r} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" name="cxl-reason" data-testid={`app-cxl-reason-${r.split(" ")[0].toLowerCase()}`}
                         checked={cxlModal.reason === r}
                         onChange={() => setCxlModal(m => ({ ...m, reason: r }))}/>
                  {r}
                </label>
              ))}
              {cxlModal.reason === "Other" && (
                <input data-testid="app-cxl-note" value={cxlModal.note}
                       onChange={e => setCxlModal(m => ({ ...m, note: e.target.value }))}
                       placeholder="Tell your coordinator what happened…" autoFocus
                       className="w-full mt-1 px-3 py-2 border border-[color:var(--aii-border)] rounded-md text-sm"/>
              )}
            </div>
            <div className="p-3 border-t border-[color:var(--aii-border)] flex items-center justify-end gap-2">
              <button onClick={() => setCxlModal(null)} className="px-3 py-2 text-sm border border-[color:var(--aii-border)] rounded-md hover:bg-slate-50">Cancel</button>
              <button data-testid="app-cxl-submit" onClick={submitCxl} disabled={busy || !cxlModal.reason}
                      className="px-3 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 font-semibold disabled:opacity-50">
                {busy ? "Sending…" : "Send request"}
              </button>
            </div>
          </div>
        </div>
      )}

      {selected.size > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 bg-brand text-white rounded-full shadow-2xl px-4 py-2.5 flex items-center gap-3 z-40" data-testid="app-bulk-bar">
          <span className="text-sm font-semibold">{selected.size} selected</span>
          <span className="text-white/50">·</span>
          <button data-testid="app-bulk-del" onClick={() => openReasonModal([...selected], "bulk")}
                  className="text-sm font-semibold px-3 py-1 rounded bg-white text-brand hover:bg-red-50 hover:text-red-700 flex items-center gap-1">
            <Trash2 size={12}/> Delete selected
          </button>
          <button onClick={clearSelected} className="text-xs text-white/70 hover:text-white p-1"><X size={13}/></button>
        </div>
      )}

      {reasonModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setReasonModal(null)}>
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-testid="app-reason-modal">
            <div className="p-4 border-b border-[color:var(--aii-border)] flex items-start justify-between">
              <div>
                <h3 className="font-display text-lg font-semibold">Why are you deleting?</h3>
                <p className="text-xs text-[color:var(--aii-muted)] mt-0.5">Our founders use this to spot cancellation trends. It won&apos;t block the delete — you can skip.</p>
                <p className="text-xs mt-1 font-semibold">{reasonModal.ids.length} application{reasonModal.ids.length === 1 ? "" : "s"} will be deleted.</p>
              </div>
              <button onClick={() => setReasonModal(null)} className="text-[color:var(--aii-muted)] hover:text-red-600 p-1"><X size={16}/></button>
            </div>
            <div className="p-4 space-y-2">
              {REASONS.map(r => (
                <label key={r} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" name="delete-reason" data-testid={`app-reason-${r.split(" ")[0].toLowerCase()}`}
                         checked={reason === r} onChange={() => setReason(r)}/>
                  {r}
                </label>
              ))}
              {reason === "Other" && (
                <input data-testid="app-reason-note" value={reasonNote} onChange={e => setReasonNote(e.target.value)}
                       placeholder="Tell Founder what happened…" autoFocus
                       className="w-full mt-1 px-3 py-2 border border-[color:var(--aii-border)] rounded-md text-sm"/>
              )}
            </div>
            <div className="p-3 border-t border-[color:var(--aii-border)] flex items-center justify-end gap-2">
              <button data-testid="app-reason-skip" onClick={() => runDeletes(reasonModal.ids, "")}
                      disabled={busy}
                      className="px-3 py-2 text-sm border border-[color:var(--aii-border)] rounded-md hover:bg-slate-50 disabled:opacity-50">
                Skip & delete
              </button>
              <button data-testid="app-reason-submit"
                      onClick={() => runDeletes(reasonModal.ids, reason === "Other" ? reasonNote.trim() : reason)}
                      disabled={busy || !reason || (reason === "Other" && !reasonNote.trim())}
                      className="px-4 py-2 text-sm bg-red-600 text-white font-semibold rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center gap-1">
                <Trash2 size={13}/> {busy ? "Deleting…" : `Delete ${reasonModal.ids.length}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Danger zone — partner-side "wipe everything" nuke. Type-to-
          confirm keeps a fat-finger tap from taking down every booking
          on the account. Recycle-Bin backed so a mistake can be
          restored by the Aii coordinator. */}
      {hasAny && (
        <div className="mt-6 aii-card p-4 border-red-200 bg-red-50/40" data-testid="partner-reset-card">
          <div className="flex items-start gap-3 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="text-xs uppercase tracking-widest text-red-700 font-bold flex items-center gap-1">
                <AlertOctagon size={12}/> Danger zone
              </div>
              <div className="text-sm font-semibold mt-0.5">Reset all my uploaded data</div>
              <div className="text-xs text-[color:var(--aii-muted)] mt-1 max-w-lg">
                Wipes every application, program booking, weekend booking, sale, invoice, payment, arrival and C-form on your account. Your login + branding stay put. Everything is snapshotted to the Recycle Bin so your Aii coordinator can restore it if fired by mistake.
              </div>
            </div>
            <button
              data-testid="partner-reset-open"
              onClick={() => { setResetOpen(true); setResetType(""); }}
              className="text-xs px-3 py-2 bg-white border border-red-500 text-red-700 rounded-md hover:bg-red-600 hover:text-white font-semibold flex items-center gap-1">
              <RotateCcw size={12}/> Reset all my data
            </button>
          </div>
        </div>
      )}

      {staffReasonModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
             onClick={() => setStaffReasonModal(null)}>
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full overflow-hidden flex flex-col"
               onClick={e => e.stopPropagation()} data-testid="staff-reason-modal">
            <div className="p-4 border-b border-[color:var(--aii-border)]">
              <h3 className="font-display text-lg font-semibold">Why are you deleting?</h3>
              <p className="text-xs text-[color:var(--aii-muted)] mt-0.5">Helps our team spot cancellation trends. Won&apos;t block the delete — skip if you prefer.</p>
              <p className="text-xs mt-1 font-semibold">
                {staffReasonModal.kind === "bulk"
                  ? `${(staffPrograms || []).length} booked participant${(staffPrograms || []).length === 1 ? "" : "s"} will be deleted.`
                  : `${staffReasonModal.target?.customer || "This participant"} will be deleted.`}
              </p>
            </div>
            <div className="p-4 space-y-2">
              {REASONS.map(r => (
                <label key={r} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" name="staff-delete-reason" data-testid={`staff-reason-${r.split(" ")[0].toLowerCase()}`}
                         checked={staffReason === r} onChange={() => setStaffReason(r)}/>
                  {r}
                </label>
              ))}
              {staffReason === "Other" && (
                <input data-testid="staff-reason-note" value={staffReasonNote} onChange={e => setStaffReasonNote(e.target.value)}
                       placeholder="Tell your coordinator what happened…" autoFocus
                       className="w-full mt-1 px-3 py-2 border border-[color:var(--aii-border)] rounded-md text-sm"/>
              )}
            </div>
            <div className="p-3 border-t border-[color:var(--aii-border)] flex items-center justify-between gap-2">
              <button onClick={() => { setStaffReasonModal(null); if (staffReasonModal.kind === "bulk") setBulkDelStaff(false); else setDelStaff(null); }}
                      className="px-3 py-2 text-sm border border-[color:var(--aii-border)] rounded-md hover:bg-slate-50">
                Cancel
              </button>
              <div className="flex items-center gap-2">
                <button onClick={submitStaffReason}
                        data-testid="staff-reason-skip"
                        className="px-3 py-2 text-sm text-[color:var(--aii-muted)] hover:text-[color:var(--aii-text-1)]">
                  Skip reason
                </button>
                <button
                  data-testid="staff-reason-submit"
                  onClick={submitStaffReason}
                  disabled={staffBusy || !staffReason || (staffReason === "Other" && !staffReasonNote.trim())}
                  className="px-4 py-2 text-sm bg-red-600 text-white font-semibold rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center gap-1">
                  <Trash2 size={13}/> Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {delStaff && !staffReasonModal && (
        <ConfirmDialog
          open={true}
          title={`Delete ${delStaff.customer}?`}
          description={`Removes the booked participant and the linked sale, arrival and C-form. Your Aii coordinator can restore it from the Recycle Bin if this was a mistake.`}
          confirmLabel={staffBusy ? "Deleting…" : "Delete"}
          onConfirm={doDelStaff}
          onCancel={() => setDelStaff(null)}
          testid="staff-delete-confirm"
        />
      )}
      {bulkDelStaff && (
        <ConfirmDialog
          open={true}
          title={`Delete all ${staffPrograms.length} booked participants?`}
          description={`Nukes every participant Aii booked under your account. All bookings + sales + arrivals + C-forms get snapshotted into ONE Recycle Bin bundle so your coordinator can restore them if this was a mistake.`}
          confirmLabel={staffBusy ? "Deleting…" : `Delete ${staffPrograms.length}`}
          onConfirm={doBulkDelStaff}
          onCancel={() => setBulkDelStaff(false)}
          testid="staff-bulk-delete-confirm"
        />
      )}
      {resetOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
             onClick={() => !resetBusy && setResetOpen(false)}>
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full overflow-hidden flex flex-col"
               onClick={e => e.stopPropagation()} data-testid="partner-reset-modal">
            <div className="p-4 border-b border-[color:var(--aii-border)] bg-red-50">
              <h3 className="font-display text-lg font-semibold flex items-center gap-2 text-red-800">
                <AlertOctagon size={18} className="text-red-600"/> Wipe every booking on your account?
              </h3>
              <p className="text-xs text-red-800 mt-1">
                This clears every application, program booking, weekend booking, sale, invoice,
                payment, arrival and C-form tied to your account. Login + branding stay put.
                Everything is snapshotted to the Recycle Bin — your Aii coordinator can restore it.
              </p>
            </div>
            <div className="p-4 space-y-3">
              <label className="text-xs font-semibold block">
                To confirm, type <code className="text-red-700 bg-red-50 px-1 rounded">{CONFIRM_PHRASE}</code>
              </label>
              <input
                data-testid="partner-reset-input"
                value={resetType}
                onChange={e => setResetType(e.target.value)}
                autoFocus
                placeholder={CONFIRM_PHRASE}
                className="w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md text-sm font-mono"/>
            </div>
            <div className="p-3 border-t border-[color:var(--aii-border)] flex items-center justify-end gap-2">
              <button onClick={() => setResetOpen(false)} disabled={resetBusy}
                      className="px-3 py-2 text-sm border border-[color:var(--aii-border)] rounded-md hover:bg-slate-50 disabled:opacity-50">
                Cancel
              </button>
              <button
                data-testid="partner-reset-submit"
                onClick={doResetMyData}
                disabled={resetBusy || resetType.trim() !== CONFIRM_PHRASE}
                className="px-4 py-2 text-sm bg-red-600 text-white font-semibold rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center gap-1">
                <RotateCcw size={13}/> {resetBusy ? "Resetting…" : "Reset everything"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
